import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TAppIdentifier, TBCTXKeys, TFailureInfo, TSingleCountEvent } from '@monorepo/types';
import { EventUtil } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import produce from 'immer';
import * as _ from 'lodash';

@Component({
  selector: 'monorepo-to-be-rectified-error-modal',
  templateUrl: './to-be-rectified-error-modal.component.html',
  styleUrls: ['./to-be-rectified-error-modal.component.scss'],
})
export class ToBeRectifiedErrorModalComponent implements OnInit {
  title = '';
  errorTxt = '';
  @Input() failedEvents: TSingleCountEvent[] = [];
  @Input() bctxKeysToDisplayMap!: Record<TBCTXKeys, string>;
  @Input() appIdentifier!: TAppIdentifier | false;
  @Input() rfidScannerBulkMode: boolean = false;
  errors: string[] = [];
  hasAnyNonUndoEvent = false;
  hasMultipleFailureTypes = true;
  failureType: TFailureInfo['failure_type'] = 'WIP_VALIDATION';
  constructor(private modalCtrl: ModalController, private translate: TranslateService) {}

  getErrorReason(event: TSingleCountEvent) {
    if (
      event.eventBackgroundMetadata.errorDetails &&
      event.eventBackgroundMetadata.errorDetails.failure_type === 'PER_PIECE_TAG_VALIDATION'
    ) {
      const reason = event.eventBackgroundMetadata.errorDetails.reason;
      if (event.eventBackgroundMetadata.errorDetails.ppt_failure_info) {
        const failureInfo = event.eventBackgroundMetadata.errorDetails.ppt_failure_info;
        switch (failureInfo.ppt_validation_type) {
          case 'INVALID_UNDO':
            if (
              !failureInfo.tag_id ||
              !failureInfo.line_number ||
              !failureInfo.department ||
              !failureInfo.dom_role ||
              !failureInfo.event_name ||
              !failureInfo.event_time
            ) {
              return reason;
            }
            return `
              ${this.translate.instant('per_piece_tag_validation_error_backend.undoEventInvalid')} 
              ${this.translate.instant('per_piece_tag_validation_error_backend.tag')} 
              ${failureInfo.tag_id}
              ${this.translate.instant('per_piece_tag_validation_error_backend.wasScannedPreviouslyAt')}
              ${failureInfo.line_number},
              ${failureInfo.department},
              ${failureInfo.dom_role},
              ${failureInfo.event_name},
              ${EventUtil.getFormatedDateFromDateTime(failureInfo.event_time)}
            `;
          case 'ORIGINAL_UNDO_EVENT_NOTFOUND':
            if (!failureInfo.tag_id) {
              return reason;
            }
            const errorReason = this.translate
              .instant('per_piece_tag_validation_error_backend.originalEventNotFound.message')
              .replace('<tag ID>', failureInfo.tag_id);
            return errorReason;
          case 'DUPLICATE_EVENT':
            return this.translate.instant('per_piece_tag_validation_error_backend.duplicatePerPieceTagId');
          case 'TAG_NEVER_SCANNED':
            if (!failureInfo.tag_id) {
              return reason;
            }

            return (
              this.translate
                .instant('per_piece_tag_validation_error_backend.neverScannedBefore.message')
                .replace('<tag Id>', failureInfo.tag_id) +
              (failureInfo.next_process_info
                ? this.translate
                    .instant('per_piece_tag_validation_error_backend.neverScannedBefore.nextStationMsg')
                    .replace(
                      '<next-station>',
                      `${failureInfo.next_process_info.department}, ${failureInfo.next_process_info.dom_role}`,
                    )
                : '')
            );
          case 'INVALID_PROCESS_ON_TOP_OF_DEFECTIVES':
            if (
              !failureInfo.tag_id ||
              !failureInfo.line_number ||
              !failureInfo.department ||
              !failureInfo.dom_role ||
              !failureInfo.event_name
            ) {
              return reason;
            }
            const invalidErrorReason = this.translate
              .instant('per_piece_tag_validation_error_backend.defectiveAlreadyAddedNew.message')
              .replace('<tag Id>', failureInfo.tag_id)
              .replace('<line number>', failureInfo.line_number)
              .replace('<station>', failureInfo.department + ', ' + failureInfo.dom_role);
            return invalidErrorReason;
          case 'CURRENT_PROCESS_NOT_FOUND_IN_CONFIG':
            if (
              !failureInfo.tag_id ||
              !failureInfo.line_number ||
              !failureInfo.department ||
              !failureInfo.dom_role ||
              !failureInfo.event_name
            ) {
              return reason;
            }
            const currentProcessNotFoundErrorReason = this.translate
              .instant('per_piece_tag_validation_error_backend.currentProcessNotFoundInConfig.message')
              .replace('<tag Id>', failureInfo.tag_id)
              .replace('<sub>', failureInfo.line_number)
              .replace('<department>', failureInfo.department)
              .replace('<dom>', failureInfo.dom_role)
              .replace('<eventName>', failureInfo.event_name);
            return currentProcessNotFoundErrorReason;
          case 'PREVIOUS_STATE_NOT_FOUND':
            if (
              !failureInfo.tag_id ||
              !failureInfo.line_number ||
              !failureInfo.department ||
              !failureInfo.dom_role ||
              !failureInfo.event_name ||
              !failureInfo.event_time
            ) {
              return reason;
            }
            const alreadyScannedError1 =
              this.translate
                .instant('per_piece_tag_validation_error_backend.tagAlreadyScannedError.message')
                .replace('<tag Id>', failureInfo.tag_id)
                .replace('<station>', failureInfo.department + ', ' + failureInfo.dom_role)
                .replace('<line>', failureInfo.line_number)
                .replace('<event-name>', failureInfo.event_name)

                .replace('<date-time>', EventUtil.getFormatedDateFromDateTime(failureInfo.event_time)) +
              (failureInfo.next_process_info
                ? this.translate
                    .instant('per_piece_tag_validation_error_backend.neverScannedBefore.nextStationMsg')
                    .replace(
                      '<next-station>',
                      `${failureInfo.next_process_info.department}, ${failureInfo.next_process_info.dom_role}`,
                    )
                : '');

            return alreadyScannedError1;
          case 'INVALID_EVENT_FOR_PREVIOUS_STATE':
            if (
              !failureInfo.tag_id ||
              !failureInfo.line_number ||
              !failureInfo.department ||
              !failureInfo.dom_role ||
              !failureInfo.event_name ||
              !failureInfo.event_time
            ) {
              return reason;
            }
            const alreadyScannedError2 =
              this.translate
                .instant('per_piece_tag_validation_error_backend.tagAlreadyScannedError.message')
                .replace('<tag Id>', failureInfo.tag_id)
                .replace('<station>', failureInfo.department + ', ' + failureInfo.dom_role)
                .replace('<line>', failureInfo.line_number)
                .replace('<event-name>', failureInfo.event_name)

                .replace('<date-time>', EventUtil.getFormatedDateFromDateTime(failureInfo.event_time)) +
              (failureInfo.next_process_info
                ? this.translate
                    .instant('per_piece_tag_validation_error_backend.neverScannedBefore.nextStationMsg')
                    .replace(
                      '<next-station>',
                      `${failureInfo.next_process_info.department}, ${failureInfo.next_process_info.dom_role}`,
                    )
                : '');

            return alreadyScannedError2;
          case 'MASTER_BUNDLE_NOT_OUT_OF_CUTBANK_OUT':
            if (!failureInfo.tag_id || !failureInfo.bundle_id) {
              return reason;
            }
            const bundle_tag_validation_error = this.translate
              .instant('to_be_rectified_error_modal.masterBundleNotOutErrorMessage')
              .replace('<tag_id>', failureInfo.tag_id)
              .replace('<bundle_id>', failureInfo.bundle_id);

            return bundle_tag_validation_error;
          default:
            return reason;
        }
      } else {
        return reason;
      }
    } else return '';
  }

  #getErrorMessage(event: TSingleCountEvent) {
    const preSizeLevelErrorsString = event.eventInfo.bctxDetails.businessContextDetailsWithoutSize
      .map((curr) => {
        return `${this.bctxKeysToDisplayMap[curr.k]}: ${curr.v}`;
      })
      .join(', ');
    const errorReason = this.getErrorReason(event);
    switch (event.actionType) {
      case 'CHECK_IN':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.checkIn',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_CHECK_IN':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoCheckIn',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'FTT':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.ftt',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_FTT':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoFtt',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'REJECTED':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.reject',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_REJECTED':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoReject',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'DEFECTIVE':
        if (event.subType === 'DEFECTIVE_FROM_DEFECTIVE')
          return errorReason
            ? errorReason
            : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
                'to_be_rectified_error_modal.logReDefective',
              )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant(
                'to_be_rectified_error_modal.itemsOf',
              )} ${
                preSizeLevelErrorsString +
                `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
              }`;
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.logDefective',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_DEFECTIVE':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoLoggedDefective',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'REJECTED_FROM_DEFECTIVE':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.rejectFromDefective',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_REJECTED_FROM_DEFECTIVE':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoRejectFromDefective',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'REWORKED_FROM_DEFECTIVE':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.rectified',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
      case 'UNDO_REWORKED_FROM_DEFECTIVE':
        return errorReason
          ? errorReason
          : `${this.translate.instant('to_be_rectified_error_modal.failedTo')} ${this.translate.instant(
              'to_be_rectified_error_modal.undoRectified',
            )} ${event.eventInfo.bctxDetails.count} ${this.translate.instant('to_be_rectified_error_modal.itemsOf')} ${
              preSizeLevelErrorsString +
              `, ${this.translate.instant('input_app_common.size')}: ${event.eventInfo.bctxDetails.size}`
            }`;
    }
  }

  #mergeEvents(list: TSingleCountEvent[]) {
    const mergedEventMap = new Map<string, TSingleCountEvent>();
    list.forEach((item) => {
      const eventKey =
        item.eventInfo.bctxDetails.bctxKey + '_' + item.eventInfo.bctxDetails.size + '_' + item.actionType;
      const event = mergedEventMap.get(eventKey);
      if (event) {
        mergedEventMap.set(
          eventKey,
          produce(event, (draft) => {
            draft.eventInfo.bctxDetails.count = draft.eventInfo.bctxDetails.count + item.eventInfo.bctxDetails.count;
          }),
        );
      } else mergedEventMap.set(eventKey, item);
    });
    const mergedEvents: TSingleCountEvent[] = [];
    mergedEventMap.forEach((event) => {
      mergedEvents.push(event);
    });
    return mergedEvents;
  }

  #updateErrors() {
    this.errors = [];
    const mergedEvents =
      this.appIdentifier === 'per_piece_line_in_v2' ||
      this.appIdentifier === 'per_piece_qc_v2' ||
      this.appIdentifier === 'md_point_per_piece_qc_v2'
        ? this.failedEvents
        : this.#mergeEvents(this.failedEvents);
    mergedEvents.forEach((event) => {
      this.errors.push(this.#getErrorMessage(event));
    });
  }

  ngOnInit(): void {
    const failureTypes = _.uniq(
      this.failedEvents.reduce((acc, event) => {
        if (event.eventBackgroundMetadata.errorDetails)
          acc.push(event.eventBackgroundMetadata.errorDetails.failure_type);
        return acc;
      }, [] as TFailureInfo['failure_type'][]),
    );
    const hasAnyNonUndoEvent = this.failedEvents.filter((event) => event.type === 'FORWARD_EVENT');
    this.hasAnyNonUndoEvent = hasAnyNonUndoEvent.length > 0;
    if (
      this.appIdentifier === 'per_piece_line_in_v2' ||
      this.appIdentifier === 'per_piece_qc_v2' ||
      this.appIdentifier === 'md_point_per_piece_qc_v2' ||
      this.appIdentifier === 'per_piece_washing_in_v2' ||
      this.appIdentifier === 'per_piece_washing_out_v2'
    ) {
      if (failureTypes.length > 1) {
        this.title = 'to_be_rectified_error_modal.validationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.validationErrorMessage';
      } else if (failureTypes[0] === 'PER_PIECE_TAG_VALIDATION') {
        this.title = 'to_be_rectified_error_modal.tagValidationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.tagValidationErrorMessage';
      } else if (failureTypes[0] === 'SOURCE_EVENT_VALIDATION') {
        this.title = 'to_be_rectified_error_modal.sourceValidationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.sourceValidationErrorMessage';
      } else if (this.hasAnyNonUndoEvent) {
        this.title = this.translate.instant('to_be_rectified_error_modal.wipValidationErrorTitle2');
        if (hasAnyNonUndoEvent.length === this.failedEvents.length) {
          this.errorTxt = this.translate.instant('to_be_rectified_error_modal.wipValidationErrorMessage3');
        } else {
          this.errorTxt = this.translate.instant('to_be_rectified_error_modal.wipValidationErrorMessage3');
        }
      } else {
        this.title = 'to_be_rectified_error_modal.undoValidationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.undoValidationErrorMessage';
      }
    } else {
      if (failureTypes.length > 1) {
        this.title = 'to_be_rectified_error_modal.validationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.validationSyncErrorMessage';
      } else if (failureTypes[0] === 'SOURCE_EVENT_VALIDATION') {
        this.title = 'to_be_rectified_error_modal.sourceEventErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.sourceEventErrorMessage';
      } else if (failureTypes[0] === 'SERVER_ERROR') {
        this.title = 'Server Error';
        this.errorTxt = 'Some Failure occurred syncing the following items, please add them again';
      } else if (this.hasAnyNonUndoEvent) {
        this.title = this.translate.instant('to_be_rectified_error_modal.wipValidationErrorTitle2');
        if (hasAnyNonUndoEvent.length === this.failedEvents.length) {
          this.errorTxt = 'to_be_rectified_error_modal.wipValidationErrorSyncMessage1';
        } else {
          this.errorTxt = 'to_be_rectified_error_modal.wipValidationErrorSyncMessage2';
        }
      } else {
        this.title = 'to_be_rectified_error_modal.undoValidationErrorTitle';
        this.errorTxt = 'to_be_rectified_error_modal.undoValidationErrorMessage';
      }
    }
    this.#updateErrors();
  }

  dismissModal() {
    this.modalCtrl.dismiss();
  }
}
